import { useTranslation } from "react-i18next";
import styles from "./Search.module.scss";
import { Box, Button } from "@mui/material";
import CollapsibleMultiselect from "../../../components/CollapsibleMultiselect";
import { useContext, useEffect, useState } from "react";
import workflowService from "../../../services/workflow.service";
import MainContext from "../../../context/mainContext";
import { SearchAdvanceFilters } from "../../../models/workflow.model";
import CollapsibleDateRange from "./CollapsibleDateRange";
import CollapsibleDepartmentDivisions from "./CollapsibleDepartmentDivisions";

interface Props {
  currentFilters: SearchAdvanceFilters;
  readonly: boolean;
  onFilter: (currentFilter: SearchAdvanceFilters) => void;
}

const SearchForm: React.FC<Props> = ({ currentFilters, readonly, onFilter }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const initFilter: SearchAdvanceFilters = {
    topics: [],
    filterBy: "byDivision",
    divisions: [],
    departments: [],
    countries: [],
    languages: [],
    fieldDate: null,
    fromDate: null,
    toDate: null,
  };

  const [filterList, setFilterList] = useState<SearchAdvanceFilters | null>(null);
  const [currentFilter, setCurrentFilter] = useState<SearchAdvanceFilters>(currentFilters || initFilter);

  const clearFilters = () => {
    const newFilter = {
      ...currentFilter,
      topics: [],
      filterBy: "byDivision",
      divisions: [],
      departments: [],
      countries: [],
      languages: [],
      fieldDate: null,
      fromDate: null,
      toDate: null,
    };

    setCurrentFilter(newFilter);
    onFilter(newFilter);
  };

  const { service } = useContext(MainContext);

  useEffect(() => {
    setLoading(true);

    service(() => workflowService.getSearchFilters(), {
      callback: (result) => {
        setFilterList(result);
        setLoading(false);
      },
    });
  }, []);

  return (
    <div className={styles.searchAdvancedForm}>
      <h3>{t("filters")}</h3>
      <div className={styles.filters}>
        <CollapsibleMultiselect
          listName={t("byTopic")}
          items={filterList?.topics}
          selectedItems={currentFilter.topics}
          readonly={readonly}
          onChangeSelected={(selected) => {
            setCurrentFilter({ ...currentFilter, topics: selected });
          }}
        />
        <CollapsibleMultiselect
          listName={t("byCountry")}
          items={filterList?.countries}
          selectedItems={currentFilter.countries}
          readonly={readonly}
          onChangeSelected={(selected) => {
            setCurrentFilter({
              ...currentFilter,
              countries: selected,
            });
          }}
        />
        <CollapsibleDateRange
          title={t("byDate")}
          fieldDate={currentFilter.fieldDate}
          fromDate={currentFilter.fromDate}
          toDate={currentFilter.toDate}
          readonly={readonly}
          onChangeSelected={(field, from, to) => {
            setCurrentFilter({
              ...currentFilter,
              fieldDate: field,
              fromDate: from,
              toDate: to,
            });
          }}
        />
        <CollapsibleDepartmentDivisions
          listName={t("byDivisionDepartment")}
          divisions={filterList?.divisions}
          departments={filterList?.departments}
          filterBy={currentFilter.filterBy}
          selectedDivisions={currentFilter.divisions}
          selectedDepartments={currentFilter.departments}
          readonly={readonly}
          onFilterChanged={(filterBy) => {
            setCurrentFilter({
              ...currentFilter,
              filterBy: filterBy,
              divisions: [],
              departments: [],
            });
          }}
          onChangeDivisionsSelected={(selected) => {
            setCurrentFilter({
              ...currentFilter,
              filterBy: "byDivision",
              divisions: selected,
              departments: [],
            });
          }}
          onChangeDepartmentsSelected={(selected) => {
            setCurrentFilter({
              ...currentFilter,
              filterBy: "byDepartment",
              departments: selected,
              divisions: [],
            });
          }}
        />
        <CollapsibleMultiselect
          listName={t("byLanguage")}
          items={filterList?.languages}
          selectedItems={currentFilter.languages}
          readonly={readonly}
          onChangeSelected={(selected) => {
            setCurrentFilter({
              ...currentFilter,
              languages: selected,
            });
          }}
          getLabel={(item) => t(`languages.${item}`)}
        />
      </div>

      <Box>
        <Button disabled={readonly} onClick={() => onFilter(currentFilter)} className={styles.filterButton}>
          {t("filter")}
        </Button>
        <Button disabled={readonly} onClick={() => clearFilters()} className={styles.clearFilterButton}>
          {t("clearFilters")}
        </Button>
      </Box>
    </div>
  );
};

export default SearchForm;
